import { AnchorHTMLAttributes, FC, MouseEvent } from 'react';
import ReactMarkdown, { Components, ExtraProps, defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { ClientLinkNavigator } from '../agency-bea/ClientLinkNavigator';

const useStyles = createUseStyles({
    markdown: {
        '& p': {
            margin: '0 0 8px 0'
        },
        '& p:last-child': {
            marginBottom: 0
        },
        '& ul, & ol': {
            margin: '8px 0',
            paddingLeft: 20
        },
        '& li': {
            marginBottom: 4
        },
        '& code': {
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
            padding: '2px 4px',
            borderRadius: 4,
            fontSize: '0.9em'
        },
        '& pre': {
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
            padding: 12,
            borderRadius: 8,
            overflowX: 'auto',
            margin: '8px 0'
        },
        '& pre code': {
            backgroundColor: 'transparent',
            padding: 0
        },
        '& a': {
            color: 'var(--ion-color-primary)',
            textDecoration: 'underline'
        },
        '& strong': {
            fontWeight: 600
        },
        '& blockquote': {
            margin: '8px 0',
            paddingLeft: 12,
            borderLeft: '3px solid rgba(0, 0, 0, 0.2)'
        }
    }
});

interface MarkdownMessageProps {
    content: string;
}

export const MarkdownMessage: FC<MarkdownMessageProps> = ({ content }) => {
    const classes = useStyles();
    const clientLinks = useInstance(ClientLinkNavigator);

    // react-markdown drops any href whose scheme it does not know, so the client scheme is let through.
    const urlTransform = (url: string): string => (clientLinks.isClientLink(url) ? url : defaultUrlTransform(url));

    const components: Components = {
        a: ({ children, href, node, ...props }: AnchorHTMLAttributes<HTMLAnchorElement> & ExtraProps) => {
            const careMapId = href ? clientLinks.careMapIdOf(href) : undefined;
            if (careMapId) {
                return (
                    <a
                        {...props}
                        href={href}
                        className="mp-mask"
                        data-id={`bea-client-link-${careMapId}`}
                        onClick={(event: MouseEvent<HTMLAnchorElement>) => {
                            event.preventDefault();
                            clientLinks.open(careMapId);
                        }}
                    >
                        {children}
                    </a>
                );
            }
            return (
                <a {...props} href={href} target="_blank" rel="noopener noreferrer">
                    {children}
                </a>
            );
        }
    };

    return (
        <div className={classes.markdown}>
            {/* remark-gfm autolinks bare URLs — the model often emits them un-bracketed, and plain
                CommonMark would render them as inert text */}
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={components} urlTransform={urlTransform}>{content}</ReactMarkdown>
        </div>
    );
};
